import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, UserCog, Layers, Handshake, Images,
  Newspaper, Quote, Inbox, Settings, ShieldCheck, UserPlus, LogOut, Menu, X, Boxes, Share2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, roles: ["content_admin", "club_admin"] },
  { to: "/admin/pages", label: "Page Content", icon: FileText, roles: ["content_admin"] },
  { to: "/admin/platforms", label: "Platforms & Suppliers", icon: Boxes, roles: ["content_admin", "club_admin"] },
  { to: "/admin/social-media", label: "Social Media", icon: Share2, roles: ["content_admin", "club_admin"] },
  { to: "/admin/coaches", label: "Coaches", icon: Users, roles: ["club_admin"] },
  { to: "/admin/staff", label: "Staff", icon: UserCog, roles: ["club_admin"] },
  { to: "/admin/age-groups", label: "Age Groups", icon: Layers, roles: ["club_admin"] },
  { to: "/admin/sponsors", label: "Sponsors", icon: Handshake, roles: ["club_admin"] },
  { to: "/admin/gallery", label: "Gallery", icon: Images, roles: ["content_admin"] },
  { to: "/admin/news", label: "News & Events", icon: Newspaper, roles: ["content_admin", "club_admin"] },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote, roles: ["content_admin", "club_admin"] },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, roles: ["club_admin"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["club_admin", "content_admin"] },
  { to: "/admin/migration", label: "Migration Review", icon: ShieldCheck, roles: ["content_admin", "club_admin"] },
  { to: "/admin/users", label: "Admin Users", icon: UserPlus, roles: [] },
];

export const AdminLayout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const visible = NAV.filter((n) => user?.role === "super_admin" || hasRole(...n.roles));

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
      isActive ? "bg-[#1E63D6] text-white" : "text-slate-300 hover:bg-white/5"
    }`;

  return (
    <div className="flex min-h-screen bg-[#0C131F] text-slate-100">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-800 bg-[#0A1626] transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 border-b border-slate-800 p-5">
          <Logo className="h-10 w-10" />
          <div><div className="font-heading text-lg font-extrabold uppercase text-white">BJRC</div><div className="text-[10px] uppercase tracking-widest text-slate-500">Admin</div></div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {visible.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="px-3 pb-2 text-xs text-slate-500">{user?.email}<br /><span className="font-bold uppercase text-[#E8A317]">{user?.role?.replace("_", " ")}</span></div>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-red-600/20 hover:text-red-400">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1">
        <header className="flex h-14 items-center gap-3 border-b border-slate-800 px-4 lg:hidden">
          <button onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <span className="font-heading font-bold uppercase">BJRC Admin</span>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
};
